import { apiFetch } from '../lib/api';

const API_BASE = '/api/node-templates';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TemplateListItem {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  source_canvas_id: string;
  source_node_id: string;
  source_canvas_name: string | null;
  source_node_exists: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ResolvedPrompt {
  id: string;
  text: string;
}

export interface ResolvedImage {
  id: string;
  type: string;
  imageUrl: string;
  originalName: string | null;
}

export interface ResolvedPreset {
  mediaType: 'image' | 'video' | 'text';
  model: string;
  size: string;
  aspectRatio: string;
  duration?: string;
  resolution?: string;
  modelSettings: Record<string, unknown>;
  customWidth?: number;
  customHeight?: number;
}

export interface TemplateInputs {
  template: {
    id: string;
    name: string;
    description: string | null;
    source_canvas_id: string;
    source_node_id: string;
  };
  prompts: ResolvedPrompt[];
  images: ResolvedImage[];
  preset: ResolvedPreset;
  sourceNode: Record<string, unknown>;
}

export interface GenerationResult {
  mediaType: 'image' | 'video' | 'text';
  imageUrl: string | null;
  videoUrl: string | null;
  thumbnailUrl?: string | null;
  textContent: string | null;
  model: string;
  prompt: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await apiFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(err.error || response.statusText);
  }
  const json = await response.json();
  return json.data as T;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export const templateHubService = {
  /** List all templates for the current org. */
  async list(): Promise<TemplateListItem[]> {
    return fetchJson<TemplateListItem[]>(API_BASE);
  },

  /** Get resolved inputs for a template. */
  async getInputs(templateId: string): Promise<TemplateInputs> {
    return fetchJson<TemplateInputs>(`${API_BASE}/${templateId}/inputs`);
  },

  /** Generate from a template with optional additional text and images. */
  async generate(
    templateId: string,
    opts: { additionalText?: string; images?: File[] } = {}
  ): Promise<GenerationResult> {
    const formData = new FormData();
    if (opts.additionalText) {
      formData.append('additional_text', opts.additionalText);
    }
    if (opts.images) {
      for (const file of opts.images) {
        formData.append('images', file);
      }
    }

    // Do NOT set Content-Type — browser sets multipart boundary automatically
    const response = await apiFetch(`${API_BASE}/${templateId}/generate`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || response.statusText);
    }

    const json = await response.json();
    return json.data as GenerationResult;
  },

  /** Update template name/description. */
  async update(
    templateId: string,
    dto: { name?: string; description?: string }
  ): Promise<TemplateListItem> {
    return fetchJson<TemplateListItem>(`${API_BASE}/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    });
  },
};
